import { useState, useEffect } from 'react';
import { Info, AlertTriangle, Wrench, Sparkles, X } from 'lucide-react';
import { fetchActivePlatformAnnouncements } from '@/lib/api';
import { cn } from '@/utils/helpers';
import type { PlatformAnnouncement, PlatformAnnouncementType } from '@/types';

const STORAGE_KEY = 'dismissed_platform_announcements';

const TYPE_CONFIG: Record<PlatformAnnouncementType, {
  icon: typeof Info;
  classes: string;
}> = {
  info: {
    icon: Info,
    classes: 'banner-blue',
  },
  warning: {
    icon: AlertTriangle,
    classes: 'banner-amber',
  },
  maintenance: {
    icon: Wrench,
    classes: 'banner-orange',
  },
  feature: {
    icon: Sparkles,
    classes: 'banner-teal',
  },
};

function getDismissedIds(): string[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function addDismissedId(id: string) {
  const current = getDismissedIds();
  if (!current.includes(id)) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...current, id]));
  }
}

export function PlatformAnnouncementBanner() {
  const [announcements, setAnnouncements] = useState<PlatformAnnouncement[]>([]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const data = await fetchActivePlatformAnnouncements();
        if (cancelled) return;

        const dismissed = getDismissedIds();
        setAnnouncements(data.filter(a => !dismissed.includes(a.id)));
      } catch (err) {
        console.error('Failed to load platform announcements:', err);
      }
    }

    load();
    return () => { cancelled = true; };
  }, []);

  const handleDismiss = (id: string) => {
    addDismissedId(id);
    setAnnouncements(prev => prev.filter(a => a.id !== id));
  };

  if (announcements.length === 0) return null;

  return (
    <div className="space-y-2">
      {announcements.map(announcement => {
        const config = TYPE_CONFIG[announcement.type] || TYPE_CONFIG.info;
        const Icon = config.icon;

        return (
          <div
            key={announcement.id}
            className={cn(
              'flex items-start gap-3 px-4 py-3 border rounded-xl text-sm',
              config.classes,
            )}
          >
            <Icon size={18} className="shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <span className="font-bold">{announcement.title}</span>
              {announcement.content && (
                <span className="ml-1.5 opacity-90">{announcement.content}</span>
              )}
            </div>
            <button
              onClick={() => handleDismiss(announcement.id)}
              className="shrink-0 p-0.5 rounded hover:bg-overlay/10 transition-colors"
              aria-label="Dismiss announcement"
            >
              <X size={16} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
